import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Camera, Calendar, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotoOfTheWeek = () => {
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPhoto();
  }, []);

  const fetchPhoto = async () => {
    try {
      const response = await axios.get(`${API}/photo-of-the-week`);
      setPhoto(response.data);
    } catch (error) {
      console.error('Error fetching photo of the week:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!photo) {
    return null;
  }

  const handleClick = () => {
    // If there's a source URL, open it in a new tab
    if (photo.source_url) {
      window.open(photo.source_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div 
      className="bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] border-2 border-black dark:border-white rounded-xl p-1 hard-shadow-lg mb-8"
      data-testid="photo-of-the-week"
    >
      <div 
        className={`bg-white dark:bg-[#2D2A26] rounded-lg overflow-hidden ${photo.source_url ? 'cursor-pointer hover:opacity-95 transition-opacity' : ''}`}
        onClick={handleClick}
      >
        {/* Header */}
        <div className="bg-black text-white px-4 py-2 flex items-center gap-2">
          <Camera className="w-5 h-5" strokeWidth={2.5} />
          <span className="font-bold text-sm uppercase tracking-wider">Photo of the Week</span>
          <div className="ml-auto flex items-center gap-1 text-xs opacity-75">
            <Calendar className="w-3 h-3" />
            <span>Week {photo.week_number}</span>
          </div>
        </div>
        
        {/* Image */}
        <div className="relative">
          <img 
            src={photo.image_url} 
            alt={photo.title}
            className="w-full h-64 object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          
          {/* Caption overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
            <p 
              className="text-white text-xl font-bold italic text-center"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              "{photo.witty_caption}"
            </p>
          </div>
        </div>
        
        {/* Title with click hint */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center gap-2">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center truncate">
              {photo.title}
            </p>
            {photo.source_url && (
              <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
            )}
          </div>
          {photo.source_url && (
            <p className="text-xs text-[#4ECDC4] text-center mt-1 font-medium">
              Click to read full story
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoOfTheWeek;
